import { useCallback, useEffect, useRef } from "react";

/** Debounces a callback. The returned function keeps a stable identity per `callback`/`delay`. */
export function useDebounce<TArgs extends unknown[]>(callback: (...args: TArgs) => void, delay = 1000) {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const debouncedCallback = useCallback(
		(...args: TArgs) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(() => {
				callback(...args);
			}, delay);
		},
		[callback, delay]
	);

	return debouncedCallback;
}

export default useDebounce;
